import { useReducer } from 'react';
import type { IGroup, IWorkspaceState } from './types';

/**
 * Workspace state + actions. A reducer keeps the many interactions (open,
 * pair, reorder, split …) in one auditable place, preserving the core
 * invariant: the pair is at most two group ids, and both must exist.
 *
 * Starts with a single empty group — no `initialFileIds` prop (decision in
 * PLAN-library-integration.md, Faza 2): the host opens files via the Explorer
 * (drag, or the context menu's "Open"/"Open to the Side"), and adds more panes
 * via the "+" button or drag & drop.
 */

let gseq = 0;
const newGroup = (fileId: string | null = null): IGroup => ({ id: `g${gseq++}`, fileId });

type Action =
  | { t: 'openInto'; groupId: string; fileId: string }
  | { t: 'openToSide'; fileId: string }
  | { t: 'selectForCompare'; fileId: string }
  | { t: 'compareWithSelected'; fileId: string }
  | { t: 'toggleCompare'; groupId: string }
  | { t: 'addGroup' }
  | { t: 'closeGroup'; groupId: string }
  | { t: 'moveGroup'; groupId: string; dir: -1 | 1 }
  | { t: 'toggleLink' };

const withPair = (s: IWorkspaceState, pair: string[]): IWorkspaceState => ({
  ...s,
  pair: pair.filter(id => s.groups.some(g => g.id === id)).slice(0, 2),
});

function reducer(s: IWorkspaceState, a: Action): IWorkspaceState {
  switch (a.t) {
    case 'openInto': {
      const groups = s.groups.map(g => (g.id === a.groupId ? { ...g, fileId: a.fileId } : g));
      return { ...s, groups };
    }
    case 'openToSide': {
      const g = newGroup(a.fileId);
      return { ...s, groups: [...s.groups, g] };
    }
    case 'selectForCompare':
      return { ...s, compareSelectId: a.fileId };
    case 'compareWithSelected': {
      // Replace the pair's two groups with the selected file and this one.
      if (!s.compareSelectId || s.compareSelectId === a.fileId) return s;
      const a0 = newGroup(s.compareSelectId);
      const b0 = newGroup(a.fileId);
      const rest = s.groups.filter(g => !s.pair.includes(g.id));
      return {
        ...s,
        groups: [a0, b0, ...rest],
        pair: [a0.id, b0.id],
        compareSelectId: null,
      };
    }
    case 'toggleCompare': {
      const at = s.pair.indexOf(a.groupId);
      const pair = at >= 0 ? s.pair.filter(id => id !== a.groupId) : [...s.pair, a.groupId];
      if (pair.length > 2) pair.shift();
      return withPair(s, pair);
    }
    case 'addGroup':
      return { ...s, groups: [...s.groups, newGroup(null)] };
    case 'closeGroup': {
      if (s.groups.length <= 1) return s;
      const groups = s.groups.filter(g => g.id !== a.groupId);
      return withPair({ ...s, groups }, s.pair.filter(id => id !== a.groupId));
    }
    case 'moveGroup': {
      const i = s.groups.findIndex(g => g.id === a.groupId);
      const j = i + a.dir;
      if (i < 0 || j < 0 || j >= s.groups.length) return s;
      const groups = s.groups.slice();
      [groups[i], groups[j]] = [groups[j], groups[i]];
      return { ...s, groups };
    }
    case 'toggleLink':
      return { ...s, link: !s.link };
    default:
      return s;
  }
}

export function useWorkspace() {
  const [state, dispatch] = useReducer(reducer, undefined, (): IWorkspaceState => {
    const g = newGroup(null);
    return { groups: [g], pair: [], link: false, compareSelectId: null };
  });

  return { state, dispatch };
}

export type { Action as WorkspaceAction };
